import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { usePermissions } from '../../../../common/auth/PermissionsContext';
import { IdentityCheckCustomer } from './hooks/useIdentityCheckCustomers';
import { FincredibleSection } from './fincredible/FincredibleSection';
import { KsvSearchSection } from './ksv/KsvSearchSection';
import { useKsvSearchForm } from './ksv/useKsvSearchForm';
import { useKsvSearch } from './ksv/useKsvSearch';

type ActiveTab = 'fincredible' | 'ksv';

interface Props {
  customer: IdentityCheckCustomer;
  dealId: number;
  onDone?: () => void;
  onUndone?: () => void;
}

export function CustomerIdentityPanel({ customer, dealId, onDone, onUndone }: Props) {
  const { hasPermission } = usePermissions();
  const canViewIdentityCheck = hasPermission('IdentityCheck', 'View');

  // Initial state derived from the customer record so the panel reflects what's
  // already on the backend when the dialog opens.
  const ksvFullyDone = customer.ksvSearchDone && !!customer.ksvId;
  const ksvWithoutHit = customer.ksvSearchDone && !customer.ksvId;

  const [fincredibleDone, setFincredibleDone] = useState(!!customer.identityCheckId);
  // ksvSelectedId: a KSV entry is selected and saved in this session
  const [ksvSelectedId, setKsvSelectedId] = useState<string | undefined>(
    customer.ksvSearchDone ? customer.ksvId : undefined
  );
  // ksvNoHit: search closed without a result → tab badge becomes warning instead of green
  const [ksvNoHit, setKsvNoHit] = useState(ksvWithoutHit);
  // done: KSV entry saved → green state
  const [done, setDone] = useState(ksvFullyDone);
  const [expanded, setExpanded] = useState(!ksvFullyDone);

  // Track the initial KSV props locally so they can be cleared on reset.
  // Passing customer.ksvId directly would re-hydrate the section to locked
  // state after Fincredible reloads the form, even after the user has reset.
  const [effectiveInitialKsvId, setEffectiveInitialKsvId] = useState(customer.ksvId);
  const [effectiveInitialNoHit, setEffectiveInitialNoHit] = useState(ksvWithoutHit);

  const [activeTab, setActiveTab] = useState<ActiveTab>(canViewIdentityCheck ? 'fincredible' : 'ksv');

  // KSV form + search live in the parent so handleFincredibleCompleted can
  // refetch the form (Fincredible just wrote new customer data) and trigger
  // the search without touching the section's mount state.
  const {
    form,
    loading: formLoading,
    refetch: refetchForm,
  } = useKsvSearchForm(customer.customerId, customer.customerType);
  const {
    search,
    results,
    isSearching,
    hasSearched,
    reset: resetSearch,
  } = useKsvSearch(customer.customerType, customer.customerId, form);

  const handleFincredibleCompleted = async () => {
    setFincredibleDone(true);
    setActiveTab('ksv');
    const hasExistingKsv = !!effectiveInitialKsvId || !!ksvSelectedId || done;
    if (hasExistingKsv) return;
    await refetchForm();
    await search();
  };

  const handleFincredibleReset = () => {
    setFincredibleDone(false);
  };

  // Saving the KSV entry completes the panel — there is no separate confirm step,
  // the user closes the dialog with the X.
  const handleKsvSelected = (ksvId: string) => {
    setKsvSelectedId(ksvId);
    setKsvNoHit(false);
    setDone(true);
    onDone?.();
  };

  const handleKsvNoHitClosed = () => {
    setKsvNoHit(true);
    setEffectiveInitialNoHit(true);
    setKsvSelectedId(undefined);
    setExpanded(false);
  };

  const handleKsvReset = () => {
    setKsvSelectedId(undefined);
    setKsvNoHit(false);
    setEffectiveInitialKsvId(undefined);
    setEffectiveInitialNoHit(false);
    if (done) {
      setDone(false);
      onUndone?.();
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, v) => setExpanded(v)}
      sx={{
        'boxShadow': 'none',
        '&:before': { display: 'none' },
        'border': '1px solid',
        'borderColor': done ? 'success.main' : ksvNoHit ? 'warning.main' : 'divider',
        'transition': 'border-color 0.2s',
      }}
      disableGutters
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {done && <CheckCircleIcon color="success" fontSize="small" />}
          {ksvNoHit && !done && <WarningAmberIcon color="warning" fontSize="small" />}
          <Typography>
            <strong>{customer.label}:</strong> {customer.displayName}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v: ActiveTab) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {canViewIdentityCheck && (
            <Tab
              value="fincredible"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  Fincredible
                  {fincredibleDone && <CheckCircleIcon fontSize="inherit" color="success" />}
                </Box>
              }
            />
          )}
          <Tab
            value="ksv"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                KSV
                {(done || ksvSelectedId !== undefined || !!effectiveInitialKsvId) && <CheckCircleIcon fontSize="inherit" color="success" />}
                {!done && ksvSelectedId === undefined && !effectiveInitialKsvId && ksvNoHit && <WarningAmberIcon fontSize="inherit" color="warning" />}
              </Box>
            }
          />
        </Tabs>

        <Box sx={{ pt: 3 }}>
          {activeTab === 'fincredible' && canViewIdentityCheck && (
            <FincredibleSection
              customerId={customer.customerId}
              dealId={dealId}
              onCompleted={handleFincredibleCompleted}
              onCanceled={() => {}}
              onReset={handleFincredibleReset}
            />
          )}

          {activeTab === 'ksv' && (
            <KsvSearchSection
              customerId={customer.customerId}
              customerType={customer.customerType}
              dealId={dealId}
              form={form}
              formLoading={formLoading}
              search={search}
              results={results}
              isSearching={isSearching}
              hasSearched={hasSearched}
              resetSearch={resetSearch}
              initialKsvId={effectiveInitialKsvId}
              initialNoHit={effectiveInitialNoHit}
              onResultSelected={handleKsvSelected}
              onNoHitClosed={handleKsvNoHitClosed}
              onReset={handleKsvReset}
            />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
